/**
 * RBAC Data Chain Component
 * 展示 ServiceAccount 的完整 RBAC 数据链
 */

import {
  InfoCard,
  StructuredMetadataTable,
} from '@backstage/core-components';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { RbacDataChain, RbacRole, RbacBinding } from './types';

const useStyles = makeStyles(theme => ({
  table: {
    minWidth: 650,
  },
  ruleItem: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    padding: theme.spacing(0.5, 1),
    margin: theme.spacing(0.25, 0),
    borderRadius: 4,
    background: 'rgba(0, 0, 0, 0.05)',
  },
  bindingRow: {
    '&:hover': {
      background: 'rgba(0, 0, 0, 0.02)',
    },
  },
}));

interface RbacDataChainViewProps {
  dataChain: RbacDataChain;
}

const RoleDetails: React.FC<{ role: RbacRole }> = ({ role }) => {
  const classes = useStyles();
  
  return (
    <Box>
      <StructuredMetadataTable
        metadata={{
          'Kind': role.kind,
          'Name': role.name,
          'Namespace': role.namespace || '(Cluster-scoped)',
          'API Version': role.apiVersion,
        }}
      />
      <Box mt={2}>
        <Typography variant="subtitle2" gutterBottom>
          权限规则 ({role.rules.length})
        </Typography>
        {role.rules.map((rule, index) => (
          <Box key={index} className={classes.ruleItem}>
            <Typography variant="caption" display="block" style={{ fontWeight: 600 }}>
              API Groups: {rule.apiGroups.join(', ') || '(core)'}
            </Typography>
            <Typography variant="caption" display="block">
              Resources: {rule.resources.join(', ')}
            </Typography>
            <Typography variant="caption" display="block">
              Verbs: {rule.verbs.join(', ')}
            </Typography>
            {rule.resourceNames && rule.resourceNames.length > 0 && (
              <Typography variant="caption" display="block">
                Resource Names: {rule.resourceNames.join(', ')}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const RbacDataChainView: React.FC<RbacDataChainViewProps> = ({ dataChain }) => {
  const classes = useStyles();

  // SA 信息
  const saMetadata: Record<string, string> = {
    'ServiceAccount': `${dataChain.serviceAccount.namespace}/${dataChain.serviceAccount.name}`,
  };
  if (dataChain.serviceAccount.uid) {
    saMetadata['UID'] = dataChain.serviceAccount.uid;
  }

  return (
    <InfoCard title="RBAC 数据链">
      <StructuredMetadataTable metadata={saMetadata} />
      
      <Divider style={{ margin: '16px 0' }} />

      {/* ClusterRoleBindings */}
      {dataChain.clusterRoleBindings.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            ClusterRoleBindings ({dataChain.clusterRoleBindings.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>名称</TableCell>
                  <TableCell>引用的 ClusterRole</TableCell>
                  <TableCell>Subjects</TableCell>
                  <TableCell>详情</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dataChain.clusterRoleBindings.map((binding, index) => (
                  <TableRow key={index} className={classes.bindingRow}>
                    <TableCell>{binding.name}</TableCell>
                    <TableCell>
                      <Chip label={binding.roleRef.name} size="small" color="primary" />
                    </TableCell>
                    <TableCell>
                      {binding.subjects.map((subject, subIndex) => (
                        <Chip
                          key={subIndex}
                          label={`${subject.kind}/${subject.name}${subject.namespace ? ` (${subject.namespace})` : ''}`}
                          size="small"
                          variant="outlined"
                          style={{ marginRight: 4, marginBottom: 4 }}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="caption">查看详情</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <StructuredMetadataTable
                            metadata={{
                              'Kind': binding.kind,
                              'Name': binding.name,
                              'API Version': binding.apiVersion,
                              'Role Kind': binding.roleRef.kind,
                              'Role Name': binding.roleRef.name,
                            }}
                          />
                        </AccordionDetails>
                      </Accordion>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* RoleBindings */}
      {dataChain.roleBindings.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            RoleBindings ({dataChain.roleBindings.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>名称</TableCell>
                  <TableCell>Namespace</TableCell>
                  <TableCell>引用的 Role/ClusterRole</TableCell>
                  <TableCell>Subjects</TableCell>
                  <TableCell>详情</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dataChain.roleBindings.map((binding, index) => (
                  <TableRow key={index} className={classes.bindingRow}>
                    <TableCell>{binding.name}</TableCell>
                    <TableCell>{binding.namespace || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={`${binding.roleRef.kind}/${binding.roleRef.name}`} 
                        size="small" 
                        color="primary" 
                      />
                    </TableCell>
                    <TableCell>
                      {binding.subjects.map((subject, subIndex) => (
                        <Chip
                          key={subIndex}
                          label={`${subject.kind}/${subject.name}${subject.namespace ? ` (${subject.namespace})` : ''}`}
                          size="small"
                          variant="outlined"
                          style={{ marginRight: 4, marginBottom: 4 }}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="caption">查看详情</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <StructuredMetadataTable
                            metadata={{
                              'Kind': binding.kind,
                              'Name': binding.name,
                              'Namespace': binding.namespace || '-',
                              'API Version': binding.apiVersion,
                              'Role Kind': binding.roleRef.kind,
                              'Role Name': binding.roleRef.name,
                            }}
                          />
                        </AccordionDetails>
                      </Accordion>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ClusterRoles */}
      {dataChain.clusterRoles.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            ClusterRoles ({dataChain.clusterRoles.length})
          </Typography>
          {dataChain.clusterRoles.map((role, index) => (
            <Accordion key={index} defaultExpanded={index === 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">{role.name}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <RoleDetails role={role} />
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Roles */}
      {dataChain.roles.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Roles ({dataChain.roles.length})
          </Typography>
          {dataChain.roles.map((role, index) => (
            <Accordion key={index} defaultExpanded={index === 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">
                  {role.name} {role.namespace && `(${role.namespace})`}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <RoleDetails role={role} />
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {dataChain.clusterRoleBindings.length === 0 && 
       dataChain.roleBindings.length === 0 && 
       dataChain.clusterRoles.length === 0 && 
       dataChain.roles.length === 0 && (
        <Box p={2} textAlign="center">
          <Typography color="textSecondary">
            未找到相关的 RBAC 配置
          </Typography>
        </Box>
      )}
    </InfoCard>
  );
};

